// DotLoader.jsx
// Laad-animatie op het 8px dot-grid van de drawer-achtergrond: een
// sonar-achtige ripple die vanuit het midden over de dots naar buiten loopt.
// Puur CSS-animaties (opacity/transform): die lopen op de compositor en blijven
// dus vloeiend terwijl de Draco-decoder (JS) de main thread bezet houdt.
import React, { useLayoutEffect, useRef } from "react";
import { DOT_LOADER, DOT_LOADER_CANVAS, DOT_LOADER_ARCH } from "../featureFlags";

// Schakelaar staat in featureFlags.js
export { DOT_LOADER };

const CELL = 8; // zelfde raster als het <pattern id="dots"> in CaseDetailDrawer
const R = 9;    // straal van het ripple-veld, in cellen
const SIZE = (2 * R + 1) * CELL;

const DOTS = (() => {
  const out = [];
  for (let y = -R; y <= R; y++) {
    for (let x = -R; x <= R; x++) {
      const d = Math.hypot(x, y);
      if (d <= R) out.push({ x, y, d });
    }
  }
  return out;
})();

const CSS = `
@keyframes dl-ripple { 0% { opacity: 0; transform: scale(0.4); } 14% { opacity: 1; transform: scale(1); } 45%, 100% { opacity: 0; transform: scale(0.4); } }
.dl-dot { position: absolute; width: 3px; height: 3px; border-radius: 0.75px; opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .dl-dot { animation: none !important; opacity: 0.4 !important; transform: scale(0.66) !important; }
}
`;

// --- DOT_LOADER_CANVAS: zelfde ripple als de CSS hierboven, in één canvas ---
const PERIOD = 1.4;
// cubic-bezier(0.32, 0.72, 0, 1), zoals CSS: per keyframe-segment toegepast
const bezier = (p1x, p1y, p2x, p2y) => {
  const cx = 3 * p1x, bx = 3 * (p2x - p1x) - cx, ax = 1 - cx - bx;
  const cy = 3 * p1y, by = 3 * (p2y - p1y) - cy, ay = 1 - cy - by;
  const sx = (t) => ((ax * t + bx) * t + cx) * t;
  const sy = (t) => ((ay * t + by) * t + cy) * t;
  const dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const e = sx(t) - x;
      if (Math.abs(e) < 1e-5) break;
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    if (Math.abs(sx(t) - x) > 1e-4) { // terugval: bisectie
      let lo = 0, hi = 1; t = x;
      for (let i = 0; i < 20; i++) { if (sx(t) < x) lo = t; else hi = t; t = (lo + hi) / 2; }
    }
    return sy(t);
  };
};
const ease = bezier(0.32, 0.72, 0, 1);
const lerp = (a, b, f) => a + (b - a) * f;
// Keyframes dl-ripple: 0% {0, 0.4} -> 14% {1, 1} -> 45%..100% {0, 0.4}
const rippleAt = (t) => {
  if (t < 0) return [0, 0.4]; // animation-fill-mode: backwards
  const p = (t % PERIOD) / PERIOD;
  if (p < 0.14) { const f = ease(p / 0.14); return [lerp(0, 1, f), lerp(0.4, 1, f)]; }
  if (p < 0.45) { const f = ease((p - 0.14) / 0.31); return [lerp(1, 0, f), lerp(1, 0.4, f)]; }
  return [0, 0.4];
};
// --- DOT_LOADER_ARCH: scankop die heen en weer over een tandboog loopt ---
const ARCH_SWEEP = 1.6; // s per richting
const ARCH_TRAIL = 18;  // samples in het staartje
const ARCH_DT = 0.028;  // s tussen de samples
// Hoefijzer (bovenaanzicht bovenkaak) in cellen t.o.v. het midden, u 0..1
const archPoint = (u) => {
  const th = (u * 2 - 1) * 0.62 * Math.PI;
  return [7 * Math.sin(th), 2.2 - 6.4 * Math.cos(th)];
};
// Heen en weer, zacht afremmen bij de uiteinden
const archU = (t) => {
  const k = (Math.max(0, t) / ARCH_SWEEP) % 2;
  const l = k < 1 ? k : 2 - k;
  return 0.5 - 0.5 * Math.cos(Math.PI * l);
};
const ARCH_PATH = Array.from({ length: 80 }, (_, i) => archPoint(i / 79));
const gauss = (d2, s) => Math.exp(-d2 / (2 * s * s));
// Per frame: staartje (posities in het verleden) + pulsje vanaf het laatste keerpunt
const archFrame = (t) => {
  const trail = [];
  for (let k = 0; k < ARCH_TRAIL; k++) {
    const [x, y] = archPoint(archU(t - k * ARCH_DT));
    trail.push([x, y, (1 - k / ARCH_TRAIL) ** 1.6]);
  }
  const tau = t % ARCH_SWEEP;
  const [px, py] = archPoint(Math.floor(t / ARCH_SWEEP) % 2 ? 1 : 0);
  return { trail, px, py, pr: tau * 7, pa: Math.max(0, 1 - tau / 0.55) * 0.55 };
};
const archShade = (d, f) => {
  let g = 0;
  for (const [x, y, w] of f.trail) {
    const v = w * gauss((d.gx - x) ** 2 + (d.gy - y) ** 2, 0.85);
    if (v > g) g = v;
  }
  if (f.pa > 0) {
    const r = Math.hypot(d.gx - f.px, d.gy - f.py) - f.pr;
    g = Math.max(g, f.pa * gauss(r * r, 0.5));
  }
  const i = Math.min(1, Math.max(d.base, g));
  return [i * 0.95, 0.4 + 0.75 * i];
};

const PAD = 2; // dots beginnen op -1px; wat ruimte rondom
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function CanvasDots() {
  const outerRef = useRef(null);
  const gridRef = useRef(null);
  const canvasRef = useRef(null);

  useAlignToGrid(outerRef, gridRef);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return undefined;
    const W = SIZE + PAD * 2;
    let dpr = 0;
    const fit = () => {
      const d = Math.min(window.devicePixelRatio || 1, 3);
      if (d === dpr) return;
      dpr = d;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(W * dpr);
    };
    const dots = DOTS.map((p) => ({
      cx: (p.x + R) * CELL - 1 + PAD + 1.5,
      cy: (p.y + R) * CELL - 1 + PAD + 1.5,
      alpha: 0.85 * Math.max(0, 1 - p.d / (R + 1)),
      delay: p.d * 0.07,
      gx: p.x,
      gy: p.y,
      // vage omtrek van de boog, zodat de vorm leesbaar blijft
      base: 0.13 * gauss(Math.min(...ARCH_PATH.map(([x, y]) => (p.x - x) ** 2 + (p.y - y) ** 2)), 0.55),
    }));
    const arch = DOT_LOADER_ARCH;
    const still = reducedMotion();
    const draw = (t) => {
      fit();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, W);
      const f = arch && !still ? archFrame(t) : null;
      for (const d of dots) {
        let o, sc, a;
        if (arch) {
          [o, sc] = still ? [d.base * 3, 0.66] : archShade(d, f);
          a = o;
        } else {
          [o, sc] = still ? [0.4, 0.66] : rippleAt(t - d.delay);
          a = o * d.alpha;
        }
        if (a < 0.004) continue;
        const h = 1.5 * sc;
        ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(d.cx - h, d.cy - h, h * 2, h * 2, 0.75 * sc);
        else ctx.rect(d.cx - h, d.cy - h, h * 2, h * 2);
        ctx.fill();
      }
    };
    if (still) { draw(0); return undefined; }
    // Niet tekenen als hij onzichtbaar is (opacity 0 van een ouder, bv. de
    // Scans-loader terwijl de PDF-tab open staat). Af en toe checken.
    const visible = () => (canvas.checkVisibility ? canvas.checkVisibility({ opacityProperty: true, visibilityProperty: true }) : true);
    let shown = true, lastCheck = -1, raf = 0, wasShown = true;
    const t0 = performance.now();
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - lastCheck > 200) { lastCheck = now; shown = visible(); }
      if (!shown) {
        if (wasShown) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); }
        wasShown = false;
        return;
      }
      wasShown = true;
      draw((now - t0) / 1000);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={outerRef} style={{ position: "relative", width: SIZE, height: SIZE, pointerEvents: "none" }}>
      <div ref={gridRef} style={{ position: "absolute", inset: 0 }}>
        <canvas ref={canvasRef} style={{ position: "absolute", left: -PAD, top: -PAD, width: SIZE + PAD * 2, height: SIZE + PAD * 2 }} />
      </div>
    </div>
  );
}

// Grid uitlijnen op de achtergrond-dots (die beginnen bij de linkerboven-
// hoek van de dots-svg). Eenmalig meten + bij resize; puur cosmetisch.
function useAlignToGrid(outerRef, gridRef) {
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const grid = gridRef.current;
    const svg = document.querySelector("pattern#dots")?.ownerSVGElement;
    if (!outer || !grid || !svg) return undefined;
    const mod = (v) => ((v % CELL) + CELL) % CELL;
    const align = () => {
      const s = svg.getBoundingClientRect();
      const o = outer.getBoundingClientRect();
      let ox = mod(s.left - o.left);
      let oy = mod(s.top - o.top);
      if (ox > CELL / 2) ox -= CELL;
      if (oy > CELL / 2) oy -= CELL;
      grid.style.transform = `translate(${ox}px, ${oy}px)`;
    };
    align();
    const ro = new ResizeObserver(align);
    ro.observe(svg);
    if (outer.parentElement) ro.observe(outer.parentElement);
    return () => ro.disconnect();
  }, [outerRef, gridRef]);
}

export default function DotLoader() {
  return DOT_LOADER_CANVAS ? <CanvasDots /> : <CssDots />;
}

function CssDots() {
  const outerRef = useRef(null);
  const gridRef = useRef(null);

  // Grid uitlijnen op de achtergrond-dots (die beginnen bij de linkerboven-
  // hoek van de dots-svg). Eenmalig meten + bij resize; puur cosmetisch.
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const grid = gridRef.current;
    const svg = document.querySelector("pattern#dots")?.ownerSVGElement;
    if (!outer || !grid || !svg) return undefined;
    const mod = (v) => ((v % CELL) + CELL) % CELL;
    const align = () => {
      const s = svg.getBoundingClientRect();
      const o = outer.getBoundingClientRect();
      let ox = mod(s.left - o.left);
      let oy = mod(s.top - o.top);
      if (ox > CELL / 2) ox -= CELL;
      if (oy > CELL / 2) oy -= CELL;
      grid.style.transform = `translate(${ox}px, ${oy}px)`;
    };
    align();
    const ro = new ResizeObserver(align);
    ro.observe(svg);
    if (outer.parentElement) ro.observe(outer.parentElement);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={outerRef} style={{ position: "relative", width: SIZE, height: SIZE, pointerEvents: "none" }}>
      <style>{CSS}</style>
      <div ref={gridRef} style={{ position: "absolute", inset: 0 }}>
        {DOTS.map((p) => {
          // Naar de rand toe zwakker, zodat de golf uitdooft
          const falloff = Math.max(0, 1 - p.d / (R + 1));
          return (
            <div
              key={`${p.x},${p.y}`}
              className="dl-dot"
              style={{
                // 3px-blokje gecentreerd op de 1px achtergrond-dot
                left: (p.x + R) * CELL - 1,
                top: (p.y + R) * CELL - 1,
                background: `rgba(255,255,255,${(0.85 * falloff).toFixed(3)})`,
                animation: `dl-ripple 1.4s cubic-bezier(0.32, 0.72, 0, 1) ${(p.d * 0.07).toFixed(3)}s infinite backwards`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
